import { useState } from 'react';
import { motion } from 'framer-motion';
import { bookingHistory } from '../../../mocks/bookingHistory';

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  completed: { label: 'Terminé', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: 'ri-check-double-line' },
  upcoming: { label: 'À venir', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: 'ri-time-line' },
  cancelled: { label: 'Annulé', color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: 'ri-close-circle-line' },
};

type FilterType = 'all' | 'completed' | 'upcoming' | 'cancelled';

export default function BookingHistory() {
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filters: { key: FilterType; label: string; icon: string }[] = [
    { key: 'all', label: 'Tous', icon: 'ri-list-check' },
    { key: 'upcoming', label: 'À venir', icon: 'ri-calendar-event-line' },
    { key: 'completed', label: 'Terminés', icon: 'ri-check-double-line' },
    { key: 'cancelled', label: 'Annulés', icon: 'ri-close-circle-line' },
  ];

  const filtered = filter === 'all'
    ? bookingHistory
    : bookingHistory.filter(b => b.status === filter);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
      day: d.getDate().toString().padStart(2, '0'),
      month: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', ''),
      year: d.getFullYear(),
      full: d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    };
  };

  return (
    <section className="mb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-night-blue flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-voyage-gold/10 rounded-xl">
            <i className="ri-history-line text-voyage-gold text-xl"></i>
          </div>
          Historique des transferts
        </h2>
        <div className="flex gap-2 flex-wrap">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full font-inter text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                filter === f.key
                  ? 'bg-night-blue text-white shadow-lg'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-voyage-gold hover:text-voyage-gold'
              }`}
            >
              <i className={`${f.icon} text-sm`}></i>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
          <i className="ri-inbox-line text-5xl text-slate-300 mb-4"></i>
          <p className="text-slate-500 font-inter text-lg">Aucune réservation trouvée</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((booking, index) => {
            const date = formatDate(booking.date);
            const status = statusConfig[booking.status];
            const isExpanded = expandedId === booking.id;

            return (
              <motion.div
                key={booking.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
                  booking.status === 'completed' ? 'border-l-4 border-l-emerald-400 border-t-slate-100 border-r-slate-100 border-b-slate-100'
                  : booking.status === 'upcoming' ? 'border-l-4 border-l-amber-400 border-t-slate-100 border-r-slate-100 border-b-slate-100'
                  : 'border-l-4 border-l-red-300 border-t-slate-100 border-r-slate-100 border-b-slate-100'
                }`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : booking.id)}
                  className="w-full p-5 sm:p-6 cursor-pointer text-left"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Date */}
                    <div className="flex-shrink-0 w-16 text-center hidden sm:block">
                      <div className="font-playfair text-3xl font-bold text-night-blue leading-none">{date.day}</div>
                      <div className="font-inter text-xs text-slate-500 uppercase mt-1">{date.month}</div>
                      <div className="font-inter text-xs text-slate-400">{date.year}</div>
                    </div>

                    {/* Separator */}
                    <div className="hidden sm:block w-px h-14 bg-slate-200"></div>

                    {/* Route info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 sm:hidden">
                        <span className="font-inter text-xs text-slate-500">{date.full}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-inter font-semibold text-night-blue text-sm sm:text-base truncate">{booking.departure}</span>
                        <i className="ri-arrow-right-line text-voyage-gold flex-shrink-0"></i>
                        <span className="font-inter font-semibold text-night-blue text-sm sm:text-base truncate">{booking.destination}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-slate-500 font-inter text-xs sm:text-sm">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {booking.heure}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-car-line"></i>
                          {booking.vehicle}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-group-line"></i>
                          {booking.passengers} pers.
                        </span>
                      </div>
                    </div>

                    {/* Status + Price */}
                    <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-inter font-semibold border ${status.bg} ${status.color}`}>
                        <i className={status.icon}></i>
                        {status.label}
                      </span>
                      <span className="font-playfair text-xl sm:text-2xl font-bold text-night-blue">{booking.price}€</span>
                    </div>

                    {/* Expand icon */}
                    <div className="hidden sm:flex items-center justify-center w-8 h-8 flex-shrink-0">
                      <i className={`ri-arrow-down-s-line text-xl text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
                    </div>
                  </div>
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-slate-100 px-5 sm:px-6 pb-5 sm:pb-6"
                  >
                    <div className="pt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 font-inter text-xs mb-1">N° Réservation</div>
                        <div className="font-inter font-bold text-night-blue text-sm">{booking.id}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 font-inter text-xs mb-1">Chauffeur</div>
                        <div className="font-inter font-bold text-night-blue text-sm">{booking.chauffeur}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 font-inter text-xs mb-1">Distance</div>
                        <div className="font-inter font-bold text-night-blue text-sm">{booking.distance} km</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 font-inter text-xs mb-1">Bagages</div>
                        <div className="font-inter font-bold text-night-blue text-sm">{booking.bagages} pièces</div>
                      </div>
                    </div>

                    {booking.status === 'completed' && booking.rating > 0 && (
                      <div className="mt-4 flex items-center gap-2">
                        <span className="text-slate-500 font-inter text-sm">Votre note :</span>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`${i < booking.rating ? 'ri-star-fill text-voyage-gold' : 'ri-star-line text-slate-300'} text-lg`}></i>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-3">
                      {booking.status === 'upcoming' && (
                        <>
                          <a
                            href="/reservation"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-voyage-gold text-night-blue rounded-full font-inter font-bold text-sm hover:bg-amber-500 transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-edit-line"></i>
                            Modifier
                          </a>
                          <a
                            href={`https://wa.me/33605606006?text=${encodeURIComponent(`Bonjour, je souhaite modifier ma réservation ${booking.id} du ${date.full}. Merci.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-full font-inter font-bold text-sm hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-whatsapp-line"></i>
                            WhatsApp
                          </a>
                        </>
                      )}
                      {booking.status === 'completed' && (
                        <a
                          href="#avis"
                          className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-night-blue text-night-blue rounded-full font-inter font-bold text-sm hover:bg-night-blue hover:text-white transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-star-line"></i>
                          Laisser un avis
                        </a>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </section>
  );
}
